// ownerContactRoster — PURE edits to the Owner Contact form's PEOPLE roster
// (moved out of pages/OwnerContactFormPage.tsx so they test under a bare
// `npx tsx --test`). Type-only imports.
//
// ⚠️ Anything that changes the roster's SHAPE (who is at which index) must also
// drop every answer that is keyed by roster index — today: ownership / funds %,
// the disbursement mode + fundsOwnerIndex, taxReporting, and §13.2 `spouses`
// (couples are index pairs). The account label is NOT index-keyed and stays — the
// server keeps it across a reshape too (API 3c9c39d6), so the form shows what will
// actually be used.

import type { OwnerContactAnswers, OwnerContactOwnerAnswer } from './ownerContactClient';

/**
 * MERGE-BACK a wrongly-split person group: collapse every person the itemizer split
 * off the SAME raw EPMA owner (sourceIndex) into ONE. Keeps the first, RESTORES the
 * original packed EPMA field (`raw` — e.g. "Miles & Nicole" / "Scruggs", not a join of
 * the split first names), clears the split flag, and strips the now-stale funds/percent
 * designation (the roster changed) so the owner re-picks over the corrected roster.
 *
 * The account label (disbursement.fundsAccountLabel) is not keyed by index, so it is
 * kept — exactly what the server keeps on a reshape (API 3c9c39d6); clearing it here
 * would show it blank while the stored label still went on the addendum.
 *
 * §13.2: it also drops `spouses`. Its couples are INDICES into this people roster, so
 * once the roster shrinks a saved couple would silently point at different people
 * (merge Bob+Carol out of [Bob, Carol, Ted, Alice, Zoe] and Ted+Alice's {a:2,b:3}
 * becomes Alice+Zoe). Cleared, the spouses question is asked again.
 */
export function mergeSplitGroup(
  answers: OwnerContactAnswers,
  sourceIndex: number,
  raw?: { firstName: string; lastName: string },
): OwnerContactAnswers {
  const people = answers.owners ?? [];
  const group = people.filter((p) => p.sourceIndex === sourceIndex);
  if (group.length <= 1) return answers;
  const survivor: OwnerContactOwnerAnswer = { ...group[0]!, fromSplit: false, confidence: 1 };
  if (raw && (raw.firstName || raw.lastName)) {
    // Restore the ORIGINAL packed name from the signed EPMA (not "Miles Nicole").
    if (raw.firstName) survivor.firstName = raw.firstName;
    else delete survivor.firstName;
    if (raw.lastName) survivor.lastName = raw.lastName;
    else delete survivor.lastName;
  } else {
    const joinedFirst = group
      .map((p) => (p.firstName ?? '').trim())
      .filter(Boolean)
      .join(' ');
    if (joinedFirst) survivor.firstName = joinedFirst;
  }
  delete survivor.ownershipPercent;
  delete survivor.fundsPercent;

  const next: OwnerContactOwnerAnswer[] = [];
  let placed = false;
  for (const p of people) {
    if (p.sourceIndex === sourceIndex) {
      if (!placed) {
        next.push(survivor);
        placed = true;
      }
      continue;
    }
    const q: OwnerContactOwnerAnswer = { ...p };
    delete q.ownershipPercent;
    delete q.fundsPercent;
    next.push(q);
  }
  const cleared: OwnerContactAnswers = { ...answers, owners: next };
  const label = answers.disbursement?.fundsAccountLabel;
  delete cleared.disbursement;
  if (label !== undefined) cleared.disbursement = { fundsAccountLabel: label };
  delete cleared.taxReporting;
  delete cleared.spouses;
  return cleared;
}
